import React, { useState, useEffect, useRef } from 'react';
import { ApiError } from '../../services';

/**
 * AdminAccountActionModal
 * Modal for confirming immediate account deletion or 6-month unblocking.
 * Both actions strictly require an admin reason and admin password.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {function} props.onClose - Modal close handler
 * @param {function} props.onSubmit - Async callback receiving { reason, password }
 * @param {'delete'|'unblock'} props.actionType - Action to perform
 * @param {string} [props.accountType='Account'] - e.g. 'Shopper' or 'Creator'
 * @param {string} [props.accountName] - Name of account being modified
 */
const AdminAccountActionModal = ({
  isOpen,
  onClose,
  onSubmit,
  actionType = 'delete',
  accountType = 'Account',
  accountName = '',
}) => {
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reasonInputRef = useRef(null);

  const isDelete = actionType === 'delete';

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setPassword('');
      setShowPassword(false);
      setError(null);
      const timer = setTimeout(() => {
        reasonInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, submitting]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedReason = reason.trim();
    const trimmedPassword = password.trim();

    if (!trimmedReason) {
      setError('Please provide a reason.');
      return;
    }
    if (!trimmedPassword) {
      setError('Please enter your admin password.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({ reason: trimmedReason, password: trimmedPassword });
      onClose?.();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : err?.code;
      if (code === 'InvalidUsernamePassword') {
        setError('Incorrect admin password. Please try again.');
      } else if (code === 'AccountLocked') {
        setError('Account temporarily locked due to multiple failed attempts. Please try again later.');
      } else if (code === 'FeatureInvalidPayload') {
        setError('Reason and admin password are required.');
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError(
          isDelete
            ? 'Failed to delete account. Please try again.'
            : 'Failed to unblock account. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = isDelete
    ? `Delete ${accountType} Account`
    : `Unblock ${accountType} Account`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-action-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) {
          onClose?.();
        }
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2
              id="admin-action-modal-title"
              className={`text-lg font-semibold ${
                isDelete ? 'text-red-600' : 'text-blue-600'
              }`}
            >
              {title}
            </h2>
            {accountName && (
              <p className="text-xs text-gray-500 mt-0.5">
                Target account: <span className="font-medium text-gray-700">{accountName}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="p-1 text-gray-400 hover:text-gray-600 rounded disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content & Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Action Explanatory / Warning Banner */}
          {isDelete ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3.5 text-xs text-red-800 space-y-1.5">
              <div className="flex items-center gap-1.5 font-semibold text-red-900">
                <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>Warning: Immediate Account Deletion</span>
              </div>
              <p>
                This account will immediately, all downlines will be reparented to the upline.
              </p>
              <p>
                The user's <strong>Phone, Tax ID, and Name</strong> will be blocked from re-registering for <strong>6 months</strong> unless unblocked by an admin.
              </p>
            </div>
          ) : (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3.5 text-xs text-blue-800 space-y-1.5">
              <div className="flex items-center gap-1.5 font-semibold text-blue-900">
                <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Notice: Lift 6-Month Re-registration Block</span>
              </div>
              <p>
                This will immediately clear the 6-month cooldown block on this account's <strong>Phone, Tax ID, and Name</strong>, allowing those credentials to register again.
              </p>
              <p>
                The account will remain in <strong>Deleted (D)</strong> status for audit and historical records.
              </p>
            </div>
          )}

          {/* Reason Input */}
          <div>
            <label
              htmlFor="admin-action-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {isDelete ? 'Deletion Reason' : 'Unblock Reason'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              id="admin-action-reason"
              ref={reasonInputRef}
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError(null);
              }}
              disabled={submitting}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              placeholder={
                isDelete
                  ? 'Explain why this account is being immediately deleted...'
                  : 'Explain why the 6-month re-registration block is being lifted...'
              }
              required
            />
          </div>

          {/* Admin Password Input */}
          <div>
            <label
              htmlFor="admin-action-password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Admin Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="admin-action-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                disabled={submitting}
                autoComplete="current-password"
                className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Enter your admin password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Required for security authorization and audit trail.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !reason.trim() || !password.trim()}
              className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors disabled:opacity-50 ${
                isDelete
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
            >
              {submitting
                ? isDelete
                  ? 'Deleting Account...'
                  : 'Unblocking Account...'
                : isDelete
                ? 'Delete Account'
                : 'Unblock Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAccountActionModal;
